import React, { useState, useEffect } from "react";
import {
  CheckCircle2,
  ChevronRight,
  BarChart3,
  Box,
  Calculator,
  Factory,
  Printer,
  ArrowRight,
  ShieldCheck,
  Smartphone,
  Zap,
  Mail,
  Phone,
  MapPin,
  Globe,
  ShoppingCart,
  CalendarDays,
  Activity,
  Truck,
} from "lucide-react";
import SalesMockup from "./components/mockups/SalesMockup";
import PurchasingMockup from "./components/mockups/PurchasingMockup";
import WarehouseMockup from "./components/mockups/WarehouseMockup";
import POMockup from "./components/mockups/POMockup";
import PrepressMockup from "./components/mockups/PrepressMockup";
import ProductionPlanMockup from "./components/mockups/ProductionPlanMockup";
import ExecutionMockup from "./components/mockups/ExecutionMockup";
import PackingMockup from "./components/mockups/PackingMockup";
import QCMockup from "./components/mockups/QCMockup";
import FinanceMockup from "./components/mockups/FinanceMockup";

const features = [
  {
    id: "sales",
    icon: <Calculator className="w-6 h-6" />,
    title: "Báo Giá & Bán Hàng",
    description:
      "Tự động tính toán costing (giá vốn) dựa trên định mức vật tư, khổ giấy và công in. Rút ngắn thời gian làm báo giá từ vài giờ xuống vài phút.",
    mockComponent: () => <SalesMockup />,
  },
  {
    id: "purchasing",
    icon: <ShoppingCart className="w-6 h-6" />,
    title: "Mua Hàng & Gia Công",
    description:
      "Lập đơn đặt hàng giấy (tờ/cuộn), vật tư in ấn. Theo dõi và quản lý các đơn hàng gia công ngoài như bế nổi, ép kim, phủ UV.",
    mockComponent: () => <PurchasingMockup />,
  },
  {
    id: "warehouse",
    icon: <Box className="w-6 h-6" />,
    title: "Kho Chuẩn Bao Bì",
    description:
      "Quản lý kho giấy theo định lượng (gsm), khổ giấy, pallet, số lô (Lot). Quét Barcode xuất/nhập, cảnh báo tồn kho và quản lý hao hụt chính xác.",
    mockComponent: () => <WarehouseMockup />,
  },
  {
    id: "po",
    icon: <Factory className="w-6 h-6" />,
    title: "Lệnh Sản Xuất (PO)",
    description:
      "Tạo Lệnh Sản Xuất với định mức BOM đa cấp phức tạp (hộp cứng, khay lót). Phân rã tự động xuống các công đoạn chi tiết.",
    mockComponent: () => <POMockup />,
  },
  {
    id: "prepress",
    icon: <Printer className="w-6 h-6" />,
    title: "Chế Bản & Khuôn Bế",
    description:
      "Kiểm soát hàng ngàn bản kẽm, khuôn bế. Quản lý hệ màu (CMYK, Pantone), trạm lpi, định vị khuôn lưu kho và cảnh báo tuổi thọ khuôn.",
    mockComponent: () => <PrepressMockup />,
  },
  {
    id: "production_plan",
    icon: <CalendarDays className="w-6 h-6" />,
    title: "Lịch Sản Xuất",
    description:
      "Xếp lịch chạy máy bằng Gantt chart trực quan. Tự động điều phối lệnh in, bế, dán giúp tối ưu công suất máy và ca làm việc.",
    mockComponent: () => <ProductionPlanMockup />,
  },
  {
    id: "execution",
    icon: <Activity className="w-6 h-6" />,
    title: "Thực Thi Sản Xuất",
    description:
      "Bảng điều khiển giám sát Real-time tốc độ máy in. Công nhân báo cáo sản lượng trực tiếp trên màn hình trạm (Tablet/Mobile) tại xưởng.",
    mockComponent: () => <ExecutionMockup />,
  },
  {
    id: "packing",
    icon: <Truck className="w-6 h-6" />,
    title: "Đóng Gói & Giao Hàng",
    description:
      "Quét Barcode kiện hàng lên xe tải. Lập lịch chuyến xe giao hàng, kiểm soát chính xác số lượng thùng/pallet giao cho từng khách hàng.",
    mockComponent: () => <PackingMockup />,
  },
  {
    id: "qc_ncr",
    icon: <ShieldCheck className="w-6 h-6" />,
    title: "Kiểm Soát Lỗi (QC)",
    description:
      "Ghi nhận sự cố phế phẩm (NCR) tận gốc rễ (5 WHYs). Chụp ảnh đính kèm lỗi rách màng, lệch màu, quy trách nhiệm và bù hao nhanh chóng.",
    mockComponent: () => <QCMockup />,
  },
  {
    id: "finance",
    icon: <BarChart3 className="w-6 h-6" />,
    title: "Tài Chính & Đối Soát",
    description:
      "Đối soát công nợ khách hàng, cấn trừ tự động chi phí phế phẩm. Duyệt chi mua hàng ngay trên Mobile, nắm bắt dòng tiền Real-time.",
    mockComponent: () => <FinanceMockup />,
  },
];

export default function LandingPage() {
  const [activeFeature, setActiveFeature] = useState(0);

  // Auto-rotate features
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % features.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  // Dynamic Branding for Landing Page
  useEffect(() => {
    const originalTitle = document.title;
    const link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
    const originalFavicon = link ? link.href : "";

    document.title = "EMS ERP | Production ERP System";
    if (link) {
      link.href = "https://gpems.net/_next/image?url=%2Flogo-ico.png&w=64&q=75";
    } else {
      const newLink = document.createElement("link");
      newLink.rel = "icon";
      newLink.href =
        "https://gpems.net/_next/image?url=%2Flogo-ico.png&w=64&q=75";
      document.head.appendChild(newLink);
    }

    return () => {
      document.title = originalTitle;
      const currentLink = document.querySelector(
        "link[rel~='icon']",
      ) as HTMLLinkElement;
      if (currentLink && originalFavicon) {
        currentLink.href = originalFavicon;
      }
    };
  }, []);

  const handleEmailClick = () => {
    window.location.href = "mailto:nguyentanhien87@gmail.com";
  };

  return (
    <div className="min-h-screen bg-white font-sans text-slate-800">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-md z-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <img
                src="https://gpems.net/_next/image?url=%2Flogo-ico.png&w=64&q=75"
                alt="EMS Logo"
                className="h-8 object-contain"
              />
              <span className="font-bold text-xl tracking-tight text-slate-900">
                EMS<span className="text-indigo-600"> ERP</span>
              </span>
            </div>
            <div className="hidden md:flex gap-8 items-center text-sm font-medium text-slate-600">
              <a
                href="#features"
                className="hover:text-indigo-600 transition-colors"
              >
                Tính Năng
              </a>
              <a
                href="#benefits"
                className="hover:text-indigo-600 transition-colors"
              >
                Lợi Ích
              </a>
              <a
                href="#contact"
                className="hover:text-indigo-600 transition-colors"
              >
                Liên Hệ
              </a>
            </div>
            <div className="flex items-center gap-4">
              <div className="hidden sm:flex items-center gap-2 text-indigo-700 font-semibold">
                <Phone className="w-4 h-4" /> 090 337 5265
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="text-center max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-50 text-indigo-700 font-medium text-sm mb-6 border border-indigo-100">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-500"></span>
            </span>
            Phiên bản Mới Nhất 2026
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight leading-tight mb-6">
            Giải Pháp Quản Trị ERP Toàn Diện Cho{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-blue-500">
              Xưởng Bao Bì
            </span>
          </h1>
          <p className="text-lg md:text-xl text-slate-600 mb-10 max-w-2xl mx-auto leading-relaxed">
            Chuyển đổi số toàn diện: Loại bỏ giấy tờ thủ công, chống thất thoát
            vật tư, tự động hóa tính giá vốn và theo dõi tiến độ sản xuất ngay
            trên màn hình điện thoại.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="#features"
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-3.5 rounded-xl font-semibold text-lg transition-all flex items-center justify-center"
            >
              Khám phá tính năng
            </a>
          </div>
        </div>
      </section>

      {/* Problem & Solution */}
      <section id="benefits" className="py-20 bg-slate-50 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Nỗi Đau Của Các Chủ Xưởng In & Bao Bì
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Và cách EMS ERP giải quyết triệt để giúp bạn an tâm điều hành
              doanh nghiệp.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* 1. CEO */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
              <div className="flex flex-row flex-nowrap items-start gap-3 mb-4">
                <div className="w-12 h-12 shrink-0 bg-red-100 text-red-600 rounded-xl flex items-center justify-center">
                  <BarChart3 className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold leading-tight mt-1">
                  CEO: Lỗ hổng tài chính, lợi nhuận ảo
                </h3>
              </div>
              <p className="text-slate-500 mb-4">
                Báo cáo tài chính chậm trễ, kinh doanh thấy doanh thu cao nhưng
                cuối năm không thấy tiền (Lãi ảo, lỗ thật do thất thoát).
              </p>
              <div className="p-4 bg-indigo-50 rounded-xl border border-indigo-100">
                <span className="text-sm font-semibold text-indigo-700 flex items-center gap-2 mb-1">
                  <CheckCircle2 className="w-4 h-4" /> Giải pháp EMS:
                </span>
                <span className="text-sm text-slate-700">
                  Dashboard tài chính Real-time. Kiểm soát chặt công nợ phải
                  thu, phải trả và dòng tiền.
                </span>
              </div>
            </div>
            {/* 2. Sales */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
              <div className="flex flex-row flex-nowrap items-start gap-3 mb-4">
                <div className="w-12 h-12 shrink-0 bg-amber-100 text-amber-600 rounded-xl flex items-center justify-center">
                  <Calculator className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold leading-tight mt-1">
                  Sales: Báo giá quá chậm & sai giá vốn
                </h3>
              </div>
              <p className="text-slate-500 mb-4">
                Mất hàng giờ lập file Excel. Đôi khi tính thiếu hao hụt vật tư
                dẫn đến báo giá lỗ hoặc báo quá cao làm mất khách hàng.
              </p>
              <div className="p-4 bg-indigo-50 rounded-xl border border-indigo-100">
                <span className="text-sm font-semibold text-indigo-700 flex items-center gap-2 mb-1">
                  <CheckCircle2 className="w-4 h-4" /> Giải pháp EMS:
                </span>
                <span className="text-sm text-slate-700">
                  Tự động tính định mức, giá vốn theo công thức chuẩn của xưởng
                  chỉ trong 2 phút.
                </span>
              </div>
            </div>
            {/* 3. Sản xuất */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
              <div className="flex flex-row flex-nowrap items-start gap-3 mb-4">
                <div className="w-12 h-12 shrink-0 bg-orange-100 text-orange-600 rounded-xl flex items-center justify-center">
                  <Factory className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold leading-tight mt-1">
                  Sản Xuất: Tiến độ mù mịt, đùn đẩy
                </h3>
              </div>
              <p className="text-slate-500 mb-4">
                Sale hối thúc, xưởng thì báo bận. Không biết đơn hàng A đang nằm
                ở máy in hay máy bế, thường xuyên trễ hẹn giao hàng.
              </p>
              <div className="p-4 bg-indigo-50 rounded-xl border border-indigo-100">
                <span className="text-sm font-semibold text-indigo-700 flex items-center gap-2 mb-1">
                  <CheckCircle2 className="w-4 h-4" /> Giải pháp EMS:
                </span>
                <span className="text-sm text-slate-700">
                  Lệnh sản xuất điện tử. Công nhân check-in từng công đoạn, cập
                  nhật tiến độ tức thời.
                </span>
              </div>
            </div>
            {/* 4. Kho */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
              <div className="flex flex-row flex-nowrap items-start gap-3 mb-4">
                <div className="w-12 h-12 shrink-0 bg-emerald-100 text-emerald-600 rounded-xl flex items-center justify-center">
                  <Box className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold leading-tight mt-1">
                  Kho: Vật tư thất thoát, hết giấy mực
                </h3>
              </div>
              <p className="text-slate-500 mb-4">
                Quản lý tồn kho lộn xộn, nhân viên xuất kho không ghi chép cẩn
                thận. Máy in đang chạy thì báo hết giấy, hết mực.
              </p>
              <div className="p-4 bg-indigo-50 rounded-xl border border-indigo-100">
                <span className="text-sm font-semibold text-indigo-700 flex items-center gap-2 mb-1">
                  <CheckCircle2 className="w-4 h-4" /> Giải pháp EMS:
                </span>
                <span className="text-sm text-slate-700">
                  Quét Barcode/QR Code xuất nhập tồn chính xác 100%. Hệ thống tự
                  động cảnh báo tồn tối thiểu.
                </span>
              </div>
            </div>
            {/* 5. Kỹ thuật */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
              <div className="flex flex-row flex-nowrap items-start gap-3 mb-4">
                <div className="w-12 h-12 shrink-0 bg-blue-100 text-blue-600 rounded-xl flex items-center justify-center">
                  <Printer className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold leading-tight mt-1">
                  Kỹ Thuật: Hàng ngàn khuôn bế thất lạc
                </h3>
              </div>
              <p className="text-slate-500 mb-4">
                Tìm khuôn bế mất cả buổi. Không theo dõi được số lần dập của
                khuôn khiến khuôn hỏng giữa chừng làm hỏng cả đơn hàng.
              </p>
              <div className="p-4 bg-indigo-50 rounded-xl border border-indigo-100">
                <span className="text-sm font-semibold text-indigo-700 flex items-center gap-2 mb-1">
                  <CheckCircle2 className="w-4 h-4" /> Giải pháp EMS:
                </span>
                <span className="text-sm text-slate-700">
                  Định danh khuôn bế bằng mã vạch. Theo dõi tuổi thọ khuôn và vị
                  trí lưu kho chính xác.
                </span>
              </div>
            </div>
            {/* 6. Chất lượng */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
              <div className="flex flex-row flex-nowrap items-start gap-3 mb-4">
                <div className="w-12 h-12 shrink-0 bg-purple-100 text-purple-600 rounded-xl flex items-center justify-center">
                  <ShieldCheck className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-bold leading-tight mt-1">
                  Chất Lượng: Lỗi hỏng (NCR) liên miên
                </h3>
              </div>
              <p className="text-slate-500 mb-4">
                Hàng lỗi (bong keo, lệch màu) xảy ra thường xuyên nhưng không
                quy được trách nhiệm, phải đền bù cho khách nhiều lần.
              </p>
              <div className="p-4 bg-indigo-50 rounded-xl border border-indigo-100">
                <span className="text-sm font-semibold text-indigo-700 flex items-center gap-2 mb-1">
                  <CheckCircle2 className="w-4 h-4" /> Giải pháp EMS:
                </span>
                <span className="text-sm text-slate-700">
                  Quy trình báo cáo NCR nghiêm ngặt. Buộc tìm ra nguyên nhân gốc
                  rễ (5 WHYs) và quy trách nhiệm rõ ràng.
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Slider */}
      <section
        id="features"
        className="py-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto"
      >
        <div className="mb-16 md:text-center max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">
            10 Tính Năng Cốt Lõi Tạo Nên Sự Khác Biệt
          </h2>
          <p className="text-slate-600">
            Hệ thống được thiết kế chuyên biệt, "may đo" sát nhất với luồng
            nghiệp vụ của các xưởng sản xuất in ấn và bao bì.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-12 items-center">
          {/* Feature List */}
          <div className="w-full lg:w-5/12 space-y-2">
            {features.map((feature, index) => (
              <div
                key={feature.id}
                onClick={() => setActiveFeature(index)}
                className={`p-5 rounded-2xl cursor-pointer transition-all duration-300 border-2 ${
                  activeFeature === index
                    ? "bg-indigo-50 border-indigo-600 shadow-md"
                    : "bg-white border-transparent hover:bg-slate-50 hover:border-slate-200"
                }`}
              >
                <div>
                  <div className="flex flex-row flex-nowrap items-start gap-3 mb-2">
                    <div
                      className={`shrink-0 p-2 rounded-lg ${activeFeature === index ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-500"}`}
                    >
                      {feature.icon}
                    </div>
                    <h3
                      className={`text-lg font-bold mt-1 ${activeFeature === index ? "text-indigo-900" : "text-slate-800"}`}
                    >
                      {feature.title}
                    </h3>
                  </div>
                  <p
                    className={`text-sm leading-relaxed ${activeFeature === index ? "text-indigo-700/80" : "text-slate-500"}`}
                  >
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Feature Preview Window */}
          <div className="w-full lg:w-7/12 relative flex items-center justify-center mt-10 lg:mt-0">
            <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-[2rem] blur opacity-20 transition duration-1000"></div>
            <div className="relative w-full aspect-video flex flex-col rounded-[2rem] bg-transparent">
              {/* Render Active Mock Component */}
              <div className="flex-1 w-full h-full relative">
                {features.map((feature, index) => (
                  <div
                    key={feature.id}
                    className={`absolute inset-0 transition-opacity duration-500 ${
                      activeFeature === index
                        ? "opacity-100 pointer-events-auto z-10"
                        : "opacity-0 pointer-events-none z-0"
                    }`}
                  >
                    {feature.mockComponent()}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quality & Standards */}
      <section className="py-20 bg-slate-900 text-white px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl font-bold mb-6">
              Vận Hành Tiêu Chuẩn Doanh Nghiệp
            </h2>
            <p className="text-slate-300 text-lg mb-8 leading-relaxed">
              EMS ERP không chỉ là phần mềm ghi chép, nó áp đặt một kỷ luật thép
              trong quản lý chất lượng (QC Check) và xử lý rủi ro (NCR). Đảm bảo
              doanh nghiệp của bạn vận hành trơn tru và chuyên nghiệp nhất trong
              mắt đối tác.
            </p>
            <div className="space-y-4">
              <div className="flex flex-row flex-nowrap items-start gap-3">
                <div className="w-10 h-10 shrink-0 rounded-full bg-indigo-500/20 flex items-center justify-center text-indigo-400">
                  <ShieldCheck className="w-5 h-5" />
                </div>
                <span className="font-medium mt-2">
                  Hệ thống form in ấn chuẩn quy trình ISO.
                </span>
              </div>
              <div className="flex flex-row flex-nowrap items-start gap-3">
                <div className="w-10 h-10 shrink-0 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400">
                  <Zap className="w-5 h-5" />
                </div>
                <span className="font-medium mt-2">
                  Công nghệ bảo mật dữ liệu cấp doanh nghiệp.
                </span>
              </div>
              <div className="flex flex-row flex-nowrap items-start gap-3">
                <div className="w-10 h-10 shrink-0 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-400">
                  <Smartphone className="w-5 h-5" />
                </div>
                <span className="font-medium mt-2">
                  Responsive hoàn hảo trên iPad, Mobile.
                </span>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-slate-800 p-6 rounded-2xl border border-slate-700 flex flex-col items-center justify-center text-center">
              <div className="text-4xl font-extrabold text-indigo-400 mb-2">
                99%
              </div>
              <div className="text-sm text-slate-400">
                Độ chính xác tồn kho khi dùng Barcode
              </div>
            </div>
            <div className="bg-slate-800 p-6 rounded-2xl border border-slate-700 flex flex-col items-center justify-center text-center mt-8">
              <div className="text-4xl font-extrabold text-emerald-400 mb-2">
                -60%
              </div>
              <div className="text-sm text-slate-400">
                Thời gian xử lý báo giá và tạo LSX
              </div>
            </div>
            <div className="bg-slate-800 p-6 rounded-2xl border border-slate-700 flex flex-col items-center justify-center text-center -mt-8">
              <div className="text-4xl font-extrabold text-amber-400 mb-2">
                24/7
              </div>
              <div className="text-sm text-slate-400">
                Kiểm soát hoạt động nhà máy Real-time
              </div>
            </div>
            <div className="bg-slate-800 p-6 rounded-2xl border border-slate-700 flex flex-col items-center justify-center text-center">
              <div className="text-4xl font-extrabold text-blue-400 mb-2">
                100%
              </div>
              <div className="text-sm text-slate-400">
                Dữ liệu được sao lưu an toàn trên Cloud
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA & Footer */}
      <footer
        id="contact"
        className="bg-slate-50 pt-20 pb-10 px-4 sm:px-6 lg:px-8 border-t border-slate-200"
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-12 pb-12 border-b border-slate-200">
            <div className="lg:col-span-1">
              <div className="flex items-center gap-2 mb-6">
                <img
                  src="https://gpems.net/_next/image?url=%2Flogo-ico.png&w=64&q=75"
                  alt="EMS Logo"
                  className="h-8 object-contain"
                />
                <span className="font-bold text-xl tracking-tight text-slate-900">
                  EMS<span className="text-indigo-600"> ERP</span>
                </span>
              </div>
              <p className="text-slate-500 mb-6 leading-relaxed">
                Giải pháp phần mềm quản trị tổng thể (ERP) tiên phong dành riêng
                cho ngành công nghiệp sản xuất và in ấn bao bì tại Việt Nam.
              </p>
            </div>
            <div className="lg:col-span-2">
              <h3 className="font-bold text-slate-900 mb-6 text-lg uppercase tracking-wider">
                Đơn vị phát triển phần mềm
              </h3>
              <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm space-y-4">
                <h4 className="font-bold text-indigo-700 text-lg">
                  CÔNG TY TNHH GIẢI PHÁP SỐ EMS
                </h4>
                <div className="grid sm:grid-cols-2 gap-4 text-sm text-slate-600">
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />
                      <span>
                        93/4 Liên khu 5-6, Phường Bình Tân, TP. Hồ Chí Minh
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-slate-400 shrink-0" />
                      <span className="font-medium text-slate-800">
                        090 337 5265 (Mr. Hiền)
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Mail className="w-5 h-5 text-slate-400 shrink-0" />
                      <a
                        href="mailto:nguyentanhien87@gmail.com"
                        className="hover:text-indigo-600"
                      >
                        nguyentanhien87@gmail.com
                      </a>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Globe className="w-5 h-5 text-slate-400 shrink-0" />
                      <a
                        href="https://www.gpems.net"
                        target="_blank"
                        rel="noreferrer"
                        className="hover:text-indigo-600"
                      >
                        www.gpems.net
                      </a>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-5 font-bold text-slate-400 shrink-0 text-center text-xs">
                        MST
                      </div>
                      <span>0316870151</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-5 font-bold text-slate-400 shrink-0 text-center text-xs">
                        STK
                      </div>
                      <div>
                        <span className="font-medium text-slate-800">
                          181119878
                        </span>{" "}
                        - ACB
                        <br />
                        <span className="text-xs">
                          Công ty TNHH Giải Pháp Số EMS
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="text-center pt-8 text-sm text-slate-500 flex flex-col md:flex-row justify-between items-center gap-4">
            <p>© 2026 EMS Solution Co., Ltd. All rights reserved.</p>
            <p>Đại diện pháp luật: Nguyễn Tấn Hiền (Giấy phép: 21/05/2021)</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
